/**
 * familyInviteService.js
 * Güvenli, tek kullanımlık aile daveti API katmanı.
 *
 * Güvenli aile davetinin oluşturma, durum, iptal ve katılım API katmanı.
 * Kalıcı aile kodu yerine FP-XXXXXXX biçimindeki tek kullanımlık kodları yönetir.
 */
import { apiRequest } from "./apiClient";
import { rt } from "../i18n/runtime";

const INVITE_PREFIX = "FP-";
const INVITE_SUFFIX_LENGTH = 7;
const INVITE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

export const FAMILY_INVITE_MASK = "••••••••";

export function normalizeFamilyInviteCode(value) {
  const compact = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "");

  if (!compact.startsWith("FP")) return "";
  const suffix = compact.slice(2);
  if (suffix.length !== INVITE_SUFFIX_LENGTH) return "";
  if ([...suffix].some((char) => !INVITE_ALPHABET.includes(char))) return "";
  return `${INVITE_PREFIX}${suffix}`;
}

export function formatFamilyInviteInput(value) {
  const compact = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 2 + INVITE_SUFFIX_LENGTH);

  if (!compact) return "";
  if (compact.length <= 2) return compact;
  return `${compact.slice(0, 2)}-${compact.slice(2)}`;
}

export function remainingInviteSeconds(expiresAt, now = Date.now()) {
  const expiryMs = new Date(expiresAt || 0).getTime();
  if (!Number.isFinite(expiryMs)) return 0;
  return Math.max(0, Math.ceil((expiryMs - Number(now)) / 1000));
}

export async function createFamilyInvite({ familyId, source = "app" }) {
  if (!familyId) throw new Error("familyId is required");
  const data = await apiRequest(`/families/${encodeURIComponent(familyId)}/invites`, {
    method: "POST",
    skipAndroidLiteHeader: true,
    body: JSON.stringify({ source }),
  });
  return data.invite || null;
}

export async function getActiveFamilyInvite({ familyId }) {
  if (!familyId) throw new Error("familyId is required");
  const data = await apiRequest(`/families/${encodeURIComponent(familyId)}/invites/active`, {
    method: "GET",
    skipAndroidLiteHeader: true,
  });
  return {
    hasActiveInvite: !!data.hasActiveInvite,
    activeInvite: data.activeInvite || null,
    canGenerateNew: data.canGenerateNew !== false,
  };
}

export async function revokeActiveFamilyInvite({ familyId }) {
  if (!familyId) throw new Error("familyId is required");
  return apiRequest(`/families/${encodeURIComponent(familyId)}/invites/active`, {
    method: "DELETE",
    skipAndroidLiteHeader: true,
  });
}

export async function joinFamilyWithInvite({ code, language } = {}) {
  const normalizedCode = normalizeFamilyInviteCode(code);
  if (!normalizedCode) {
    const error = new Error(rt("familyInvite.errors.invalid"));
    error.code = "INVITE_CODE_INVALID";
    throw error;
  }
  return apiRequest("/families/join", {
    method: "POST",
    skipAndroidLiteHeader: true,
    body: JSON.stringify({
      code: normalizedCode,
      language: language || (() => { try { return localStorage.getItem("appLanguage") || "tr"; } catch { return "tr"; } })(),
    }),
  });
}
